if (!customElements.get('complete-the-look')) {
  customElements.define(
    'complete-the-look',
    class CompleteTheLook extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-item]'));
        this.button = this.querySelector('[data-add]');
        this.dialog = this.querySelector('[data-dialog]');

        this.items.forEach((item) => {
          item.querySelector('[data-include]').addEventListener('change', () => this.update());
          item.querySelector('[data-zoom]')?.addEventListener('click', (event) => this.openZoom(event, item));

          const sheet = item.querySelector('[data-sheet]');
          if (!sheet) return;
          item.querySelector('[data-sheet-open]').addEventListener('click', () => this.openSheet(item));
          // Tapping the photo opens the same sheet (the zoom button on it keeps its own action).
          // Mouse/touch shortcut only: keyboard users reach the sheet through the size button.
          const media = item.querySelector('.complete-the-look__media');
          media.classList.add('is-clickable');
          media.addEventListener('click', (event) => {
            if (!event.target.closest('[data-zoom]')) this.openSheet(item);
          });
          sheet.querySelector('[data-sheet-close]').addEventListener('click', () => sheet.close());
          this.closeOnBackdrop(sheet);
          sheet.querySelectorAll('[data-size]').forEach((size) =>
            size.addEventListener('click', () => {
              if (size.disabled) return;
              this.applyVariant(item, size);
              sheet.close();
            })
          );
          this.initGallery(sheet);
        });

        this.button.addEventListener('click', () => this.addToCart());
        this.querySelector('[data-dialog-close]').addEventListener('click', () => this.dialog.close());
        this.closeOnBackdrop(this.dialog);

        // The current product's row follows the main variant picker.
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            // Empty data-section-id (standalone section): follow the page's one main product picker.
            if (this.dataset.sectionId && event.data.sectionId !== this.dataset.sectionId) return;
            this.syncCurrent(event.data.variant);
          });
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      // A click on the backdrop lands on the dialog element itself, not on anything inside it.
      closeOnBackdrop(dialog) {
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      }

      openSheet(item) {
        const sheet = item.querySelector('[data-sheet]');
        // No size chosen yet: start on the first size in stock, so the row is never left empty.
        if (!item.dataset.variantId) {
          const first = sheet.querySelector('[data-size]:not([disabled])');
          if (first) this.applyVariant(item, first);
        }
        sheet.showModal();
        sheet.querySelector('[aria-pressed="true"]')?.focus();
      }

      syncCurrent(variant) {
        const item = this.items.find((row) => row.hasAttribute('data-current'));
        if (!item || !variant) return;
        const size = item.querySelector(`[data-size][data-variant-id="${variant.id}"]`);
        if (size && !size.disabled) this.applyVariant(item, size);
      }

      // `source` is a size button in the sheet; it carries the variant's id, prices and images.
      applyVariant(item, source) {
        const { variantId, price, compare, label, image, zoom } = source.dataset;
        item.dataset.variantId = variantId;
        item.dataset.price = price;
        item.dataset.compare = compare;
        this.hideItemError(item);

        item.querySelectorAll('[data-size]').forEach((size) =>
          size.setAttribute('aria-pressed', size === source ? 'true' : 'false')
        );
        item.querySelector('[data-size-label]').textContent = label;

        const priceText = this.formatMoney(Number(price));
        const compareText = this.formatMoney(Number(compare));
        const onSale = Number(compare) > Number(price);
        [
          ['[data-item-price]', '[data-item-compare]'],
          ['[data-sheet-price]', '[data-sheet-compare]'],
        ].forEach(([priceSelector, compareSelector]) => {
          const priceEl = item.querySelector(priceSelector);
          const compareEl = item.querySelector(compareSelector);
          if (priceEl) priceEl.textContent = priceText;
          if (compareEl) {
            compareEl.textContent = compareText;
            compareEl.hidden = !onSale;
          }
        });

        const badge = item.querySelector('[data-item-badge]');
        if (badge) {
          badge.textContent = onSale ? `Save ${this.formatMoney(Number(compare) - Number(price))}` : '';
          badge.hidden = !onSale;
        }

        // Swap the row thumbnail to the variant's own image when it has one.
        const thumbnail = item.querySelector('.complete-the-look__image');
        if (thumbnail && image) {
          thumbnail.removeAttribute('srcset');
          thumbnail.src = image;
          const zoomButton = item.querySelector('[data-zoom]');
          if (zoomButton) zoomButton.dataset.zoom = zoom;
        }

        this.update();
      }

      initGallery(sheet) {
        const track = sheet.querySelector('[data-gallery-track]');
        if (!track) return;
        const slides = Array.from(track.children);
        const index = sheet.querySelector('[data-gallery-index]');
        const current = () => Math.round(track.scrollLeft / track.clientWidth);
        const goTo = (i) => {
          const target = slides[(i + slides.length) % slides.length];
          track.scrollTo({ left: target.offsetLeft, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        };

        sheet.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(current() - 1));
        sheet.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(current() + 1));
        track.addEventListener('scroll', () => {
          if (index) index.textContent = current() + 1;
        });
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // Tier discounts from data-tiers="2:5,3:10,4:15" (quantity:percent). Display only: checkout
      // applies the matching automatic discounts set up in Shopify admin.
      get tiers() {
        if (!this._tiers) {
          this._tiers = (this.dataset.tiers || '')
            .split(',')
            .map((pair) => pair.split(':').map(Number))
            .filter(([quantity, percent]) => quantity > 0 && percent > 0)
            .map(([quantity, percent]) => ({ quantity, percent }))
            .sort((a, b) => a.quantity - b.quantity);
        }
        return this._tiers;
      }

      update() {
        const checked = this.checkedItems();
        const discountable = checked.filter((item) => item.hasAttribute('data-discountable'));
        const tier = this.tiers.filter((t) => discountable.length >= t.quantity).pop();
        const percent = tier ? tier.percent : 0;

        let total = 0;
        let compareTotal = 0;
        checked.forEach((item) => {
          const price = Number(item.dataset.price);
          // Shopify takes the percentage off each line and rounds it to the cent, so do the same.
          const discount = item.hasAttribute('data-discountable') ? Math.round((price * percent) / 100) : 0;
          total += price - discount;
          compareTotal += price;
        });
        const count = checked.length;
        const savings = compareTotal - total;

        this.querySelector('[data-total-label]').textContent = this.plural('total', count);
        this.querySelector('[data-total]').textContent = this.formatMoney(total);
        const compareEl = this.querySelector('[data-compare-total]');
        compareEl.textContent = this.formatMoney(compareTotal);
        compareEl.hidden = savings <= 0;
        const savingsEl = this.querySelector('[data-savings]');
        savingsEl.textContent = (percent ? this.dataset.savePercent : this.dataset.save)
          .replace('[amount]', this.formatMoney(savings))
          .replace('[percent]', percent);
        savingsEl.hidden = savings <= 0;

        // Point to the next tier, but only when ticking more rows here can reach it.
        const next = this.tiers.find((t) => t.quantity > discountable.length);
        const reachable = this.items.filter((item) => item.hasAttribute('data-discountable')).length;
        const nudgeEl = this.querySelector('[data-nudge-text]');
        const showNudge = Boolean(next) && next.quantity <= reachable;
        nudgeEl.hidden = !showNudge;
        if (showNudge) {
          nudgeEl.textContent = this.dataset.nudge
            .replace('[count]', next.quantity - discountable.length)
            .replace('[percent]', next.percent);
        }

        this.querySelector('[data-button-label]').textContent = this.plural('button', count);
        this.querySelector('[data-totals]').hidden = count === 0;
        this.querySelector('[data-empty]').hidden = count !== 0;
        this.button.disabled = count === 0;
      }

      checkedItems() {
        return this.items.filter((item) => item.querySelector('[data-include]').checked);
      }

      plural(key, count) {
        if (count === 0 && this.dataset[`${key}Zero`]) return this.dataset[`${key}Zero`];
        const text = count === 1 ? this.dataset[`${key}One`] : this.dataset[`${key}Other`];
        return text.replace('[count]', count);
      }

      // Same formats Shopify's `money` filter uses, so totals match the server-rendered prices.
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const value = cents / 100;
        const number = (decimals, thousands, decimal) => {
          const [whole, fraction] = value.toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        const formats = {
          amount: () => number(2, ',', '.'),
          amount_no_decimals: () => number(0, ',', '.'),
          amount_with_comma_separator: () => number(2, '.', ','),
          amount_no_decimals_with_comma_separator: () => number(0, '.', ','),
          amount_with_apostrophe_separator: () => number(2, "'", '.'),
          amount_no_decimals_with_space_separator: () => number(0, ' ', ','),
          amount_with_space_separator: () => number(2, ' ', ','),
          amount_with_period_and_space_separator: () => number(2, ' ', '.'),
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => (formats[key] || formats.amount)());
      }

      openZoom(event, item) {
        // Built on demand so the 1200px image only downloads when someone asks for it.
        const image = document.createElement('img');
        image.className = 'complete-the-look__dialog-image';
        image.src = event.currentTarget.dataset.zoom;
        image.alt = item.querySelector('.complete-the-look__image')?.alt || '';
        this.dialog.querySelector('[data-dialog-media]').replaceChildren(image);
        this.dialog.showModal();
      }

      showItemError(item, message) {
        const error = item.querySelector('[data-item-error]');
        error.textContent = message;
        error.hidden = false;
      }

      hideItemError(item) {
        const error = item.querySelector('[data-item-error]');
        error.hidden = true;
        error.textContent = '';
      }

      showError(message) {
        const error = this.querySelector('[data-error]');
        error.textContent = message;
        error.hidden = !message;
      }

      async addToCart() {
        this.showError('');
        const missing = this.checkedItems().filter((item) => !item.dataset.variantId);
        missing.forEach((item) =>
          this.showItemError(item, this.dataset.selectError.replace('[option]', item.dataset.optionName))
        );
        if (missing.length) {
          missing[0].querySelector('[data-sheet-open]')?.focus();
          return;
        }

        const items = this.checkedItems().map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
        if (!items.length) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        this.button.classList.add('loading');
        this.button.setAttribute('aria-disabled', 'true');
        this.button.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const state = await response.json();

          if (state.status) {
            this.showError(state.description || state.message);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'complete-the-look',
                errors: state.errors || state.description,
                message: state.message,
              });
            }
            return;
          }

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-the-look', cartData: state });
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (cart.tagName === 'CART-NOTIFICATION') {
            this.renderNotification(cart, state);
          } else {
            cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
            cart.renderContents(state);
          }
        } catch (error) {
          console.error(error);
          this.showError(window.cartStrings?.error || 'Something went wrong. Please try again.');
        } finally {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
          this.button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      // Dawn's notification shows one product (by line key). Render it for the first product, then
      // replace its contents with every product just added.
      renderNotification(cart, state) {
        cart.renderContents({ ...state, key: state.items[0].key });
        if (state.items.length < 2) return;

        const html = new DOMParser().parseFromString(state.sections['cart-notification-product'], 'text/html');
        const rows = state.items
          .map((line) => html.querySelector(`[id="cart-notification-product-${line.key}"]`))
          .filter(Boolean);
        document.getElementById('cart-notification-product').innerHTML = rows.map((row) => row.outerHTML).join('');
      }
    }
  );
}
